import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
// eslint-disable-next-line max-len
import { PreferenceContext, SPECIFIC_USE } from "@comunicare/comunicare-interfaces";
import { CareplansListPageComponent } from "./careplan-editor/views/careplans-list-page/views/careplans-list-page/careplans-list-page.component";
import { CareplanComponent } from "./components/careplan/careplan.component";
import { DefinitionListComponent } from "./components/definition-list/definition-list.component";
import { PatientFormComponent } from "./components/patient-form/patient-form.component";
import { PrescriptionFormComponent } from "./components/prescription-form/prescription-form.component";
import { RelatedFormComponent } from "./components/related-form/related-form.component";
import { VitalSignsFormComponent } from "./components/vital-signs-form/vital-signs-form.component";
import { AuthGuard } from "./guards/auth.guard";
import { LoginGuard } from "./guards/login.guard";
import { PermissionGuard } from "./guards/permission.guard";
import { RouteData } from "./models/route-data.interface";
import { AccessGroupsPageComponent } from "./pages/access-groups-page/access-groups-page.component";
import { BillingPageComponent } from "./pages/billing/components/billing-page/billing-page.component";
import { CommunicationsPageComponent } from "./pages/communications-page/communications-page.component";
import { CorrespondantPageComponent } from "./pages/correspondant-page/correspondant-page.component";
import { DashboardPageComponent } from "./pages/dashboard-page/dashboard-page.component";
import { DataExtractionComponent } from "./pages/data-extraction/data-extraction.component";
import { DeleteRequestListPageComponent } from "./pages/delete-request-list-page/delete-request-list-page.component";
import { DrugSchemaPageComponent } from "./pages/drug-schema-page/drug-schema-page.component";
import { ErrorPageComponent } from "./pages/error-page/error-page.component";
import { GroupsListPageComponent } from "./pages/groups-list-page/groups-list-page.component";
import { IdentityVerifMethodsPageComponent } from "./pages/identity-verif-methods-page/identity-verif-methods-page.component";
import { InstallComunicarePageComponent } from "./pages/install-comunicare-page/install-comunicare-page.component";
import { JournalInsiPageComponent } from "./pages/journal-insi-page/journal-insi-page.component";
import { KnowledgeListContainerPageComponent } from "./pages/knowledge-list-page/knowledge-list-container-page.component";
import { LinkPageComponent } from "./pages/link-page/link-page.component";
import { LoginPageComponent } from "./pages/login-page/login-page.component";
import { LogoutPageComponent } from "./pages/logout-page/logout-page.component";
import { MyServicePageComponent } from "./pages/my-service-page/my-service-page.component";
import { NotFoundPageComponent } from "./pages/not-found-page/not-found-page.component";
import { OrganizationListPageComponent } from "./pages/organization-list-page/organization-list-page.component";
import { PatientEditionPageComponent } from "./pages/patient-edition-page/patient-edition-page.component";
import { PatientListPageComponent } from "./pages/patient-list-page/patient-list-page.component";
import { SimplePatientListPageComponent } from "./pages/patient-list-page/simple-patient-list-page/simple-patient-list-page.component";
import { PatientPageComponent } from "./pages/patient-page/patient-page.component";
import { PatientRegistrationPageComponent } from "./pages/patient-registration-page/patient-registration-page.component";
import { PreferencePageComponent } from "./pages/preference-page/preference-page.component";
import { PrescriptionListPageComponent } from "./pages/prescription-list-page/prescription-list-page.component";
import { QuestionnaireDefListPageComponent } from "./pages/questionnaire-def-list-page/questionnaire-def-list-page.component";
import { QuestionnaireListPageComponent } from "./pages/questionnaire-list-page/questionnaire-list-page.component";
import { QuizListPageComponent } from "./pages/quiz-list-page/quiz-list-page.component";
import { RewardsPageComponent } from "./pages/rewards-page/rewards-page.component";
import { RuleEditorContainerPageComponent } from "./pages/rule-editor-page/rule-editor-container-page.component";
import { RuleListContainerPageComponent } from "./pages/rule-list-page/rule-list-container-page.component";
import { StatEventsPageComponent } from "./pages/statistics-pages/stat-events-page/stat-events-page.component";
import { StatSummaryPageComponent } from "./pages/statistics-pages/stat-summary-page/stat-summary-page.component";
import { TeleconsultationsPageComponent } from "./pages/teleconsultation-page/teleconsultation-page.component";
import { TemplateQuestionnaireListPageComponent } from "./pages/template-questionnaire-list-page/template-questionnaire-list-page.component";
import { TpdasPageComponent } from "./pages/tpdas-page/tpdas-page.component";
import { UserPageComponent } from "./pages/user-page/user-page.component";
import { DeleteRequestResolver } from "./providers/resolvers/deleteRequest.resolver";
import { KnowledgeResolver } from "./providers/resolvers/knowledge.resolver";
import { KnowledgeDetailsResolver } from "./providers/resolvers/knowledgeDetails.resolver";
import { PatientPageResolver } from "./providers/resolvers/patient-page.resolver";
import { PatientsCountResolver } from "./providers/resolvers/patient.resolver";
import { PreferenceResolver } from "./providers/resolvers/preferences.resolver";
import { QuizListResolver } from "./providers/resolvers/quizList.resolver";

const routes: Routes = [
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
  { path: "login", component: LoginPageComponent, canActivate: [LoginGuard] },
  { path: "logout", component: LogoutPageComponent },
  {
    path: "dashboard",
    component: DashboardPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "patients",
    component: PatientListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      patientsCount: PatientsCountResolver,
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,
      ctx: PreferenceContext.PATIENT_LIST,
    } as RouteData,
  },
  {
    path: "simplePatientsList",
    component: SimplePatientListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      patientsCount: PatientsCountResolver,
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,
      ctx: PreferenceContext.PATIENT_LIST,
    } as RouteData,
  },
  // { path: "preference", component: PreferencePageComponent, canActivate: [AuthGuard, PermissionGuard], data: {
  //         canBeLanding: true,
  //         featureGuardKey: PermissionFeatures.DASHBOARD_MENU_PREFERENCE
  //     } as RouteData
  // },
  {
    path: "accessGroups",
    component: AccessGroupsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "patient",
    component: PatientPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      configuration: PatientPageResolver,
    },
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "teleconsultations",
    component: TeleconsultationsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "organizations",
    component: OrganizationListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "correspondant",
    component: CorrespondantPageComponent,
    canActivate: [AuthGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "myService",
    component: MyServicePageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "groups",
    component: GroupsListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "questionnaireList",
    component: QuestionnaireListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "communications",
    component: CommunicationsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "rewardsPage",
    component: RewardsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "templatesQuestionnaire",
    component: TemplateQuestionnaireListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "fhirQuestionnaireList",
    component: QuestionnaireDefListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: false,
      ctx: PreferenceContext.FHIR_QUESTIONNAIRE_PAGE,
    } as RouteData,
  },
  {
    path: "questionnaireEditor",
    loadChildren: () =>
      import("./pages/questionnaire-editor-page/questionnaire-editor-container-page.module").then(
        (m) => m.QuestionnaireEditorContainerPageModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "drugSchema",
    component: DrugSchemaPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "knowledgeList",
    component: KnowledgeListContainerPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      knowledgeCount: KnowledgeResolver,
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,
      ctx: PreferenceContext.KNOWLEDGE_LIST,
    } as RouteData,
  },
  {
    path: "knowledgeDetails",
    loadChildren: () =>
      import("./pages/knowledge-details-page/knowledge-details-container-page.module").then((m) => m.KnowledgeDetailsContainerPageModule),
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      knowledgeData: KnowledgeDetailsResolver,
    },
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "knowledgeMediaEditor",
    loadChildren: () =>
      import("./pages/knowledge-media-editor/knowledge-media-editor-container.module").then((m) => m.KnowledgeMediaEditorContainerModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "quizList",
    component: QuizListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      quizCount: QuizListResolver,
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: false,
      specificUse: SPECIFIC_USE.QUIZ,
      ctx: PreferenceContext.QUIZ_PAGE,
    } as RouteData,
  },
  {
    path: "consentList",
    component: QuizListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      quizCount: QuizListResolver,
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: false,
      specificUse: SPECIFIC_USE.CONSENT,
      ctx: PreferenceContext.CONSENTS_PAGE,
    } as RouteData,
  },
  {
    path: "deleteRequestList",
    component: DeleteRequestListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      deleteRequestCount: DeleteRequestResolver,
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,
      ctx: PreferenceContext.DELETE_REQUEST_PAGE,
    } as RouteData,
  },
  {
    path: "user",
    component: UserPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "statSummary",
    component: StatSummaryPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "statEvents",
    component: StatEventsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: false,
      ctx: PreferenceContext.STAT_EVENTS_PAGE,
    } as RouteData,
  },
  {
    path: "statEventsPrivate",
    component: StatEventsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: false,
      ctx: PreferenceContext.STAT_EVENTS_PAGE,
      includePrivateLogs: true,
    } as RouteData,
  },
  {
    path: "journalInsi",
    component: JournalInsiPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "identityVerifMethods",
    component: IdentityVerifMethodsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "dataExtraction",
    component: DataExtractionComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "careplansList",
    component: CareplansListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,

      ctx: PreferenceContext.CAREPLAN_LIST,
    } as RouteData,
  },
  {
    path: "patientRegistration",
    component: PatientRegistrationPageComponent,
    canActivate: [AuthGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
    children: [
      {
        path: "patient",
        component: PatientFormComponent,
        canActivate: [AuthGuard],
        data: {
          canBeLanding: false,
        } as RouteData,
      },
      {
        path: "relateds",
        component: RelatedFormComponent,
        canActivate: [AuthGuard],
        data: {
          canBeLanding: false,
        } as RouteData,
      },
      {
        path: "careplan",
        component: CareplanComponent,
        canActivate: [AuthGuard],
        data: {
          canBeLanding: false,
        } as RouteData,
      },
      {
        path: "vitalSigns",
        component: VitalSignsFormComponent,
        canActivate: [AuthGuard],
        data: {
          canBeLanding: false,
        } as RouteData,
      },
      {
        path: "observationDefinitions",
        component: DefinitionListComponent,
        canActivate: [AuthGuard],
        data: {
          canBeLanding: false,
          context: "Observation",
        } as RouteData,
      },
      {
        path: "prescription",
        component: PrescriptionFormComponent,
        canActivate: [AuthGuard],
        data: {
          canBeLanding: false,
        } as RouteData,
      },
    ],
  },
  {
    path: "invoices",
    component: BillingPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,
      ctx: PreferenceContext.INVOICE_LIST,
    } as RouteData,
  },
  {
    path: "preferences",
    component: PreferencePageComponent,
    canActivate: [AuthGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "prescriptionsList",
    component: PrescriptionListPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    resolve: {
      preferences: PreferenceResolver,
    },
    data: {
      canBeLanding: true,
      ctx: PreferenceContext.PRESCRIPTIONS_LIST,
    } as RouteData,
  },
  {
    path: "patientEdition/:id",
    component: PatientEditionPageComponent,
    canActivate: [AuthGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "tpdas",
    component: TpdasPageComponent,
    canActivate: [AuthGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  {
    path: "rules",
    component: RuleListContainerPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: true,
    } as RouteData,
  },
  {
    path: "ruleDetails",
    component: RuleEditorContainerPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      canBeLanding: false,
    } as RouteData,
  },
  { path: "installComunicare", component: InstallComunicarePageComponent },
  { path: "error", component: ErrorPageComponent },
  { path: "link", component: LinkPageComponent },
  {
    path: "**",
    component: NotFoundPageComponent,
    data: {
      hideTitle: true,
    },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
